import { writable } from 'svelte/store';
import fetch from '/util';

export const installPrompt = writable(null);
export const waitingWorker = writable(null);
const OWNER_KEY = 'cmyk-push-owner';
let registrationPromise;

export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
}

export function initPWA() {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    installPrompt.set(event);
  });
  window.addEventListener('appinstalled', () => installPrompt.set(null));
  if (window.isSecureContext && 'serviceWorker' in navigator) {
    registrationPromise = navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' }).then(registration => {
      if (registration.waiting) waitingWorker.set(registration.waiting);
      const trackUpdate = () => {
        const worker = registration.installing;
        worker?.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) waitingWorker.set(worker);
        });
      };
      registration.addEventListener('updatefound', trackUpdate);
      trackUpdate();
      return registration;
    });
    // 서비스 워커는 프로덕션 빌드에서만 제공된다. 개발 중 등록 실패는 업무를 막지 않는다.
    registrationPromise.catch(() => {});
  }
}

export async function getPWARegistration() {
  const registration = await registrationPromise;
  if (!registration) throw new Error('앱을 준비하지 못했습니다. 새로고침 후 다시 시도해주세요.');
  if (registration.active) return registration;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('앱 준비가 지연되고 있습니다. 잠시 후 다시 시도해주세요.')), 15000);
    navigator.serviceWorker.ready.then(value => { clearTimeout(timer); resolve(value); });
  });
}

export function pushSupported() {
  return window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function owner() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user.ID && user.WorkspaceID ? `${user.WorkspaceID}:${user.ID}` : '';
}

export async function pushAPI(path, method = 'GET', body) {
  const response = await fetch(`/push/${path}`, {
    method, headers: { 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
    signal: AbortSignal.timeout(15000),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.msg || '알림 설정을 처리하지 못했습니다.');
  return result;
}

export async function currentSubscription() {
  return (await getPWARegistration()).pushManager.getSubscription();
}

export async function reconcilePushOwner() {
  if (!pushSupported()) return;
  const saved = localStorage.getItem(OWNER_KEY);
  if (saved === owner()) return;
  const subscription = await currentSubscription();
  if (subscription && !(await subscription.unsubscribe())) throw new Error('기존 계정의 알림을 해제하지 못했습니다.');
  localStorage.removeItem(OWNER_KEY);
}

function publicKeyBytes(value) {
  return Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), char => char.charCodeAt(0));
}

export function subscriptionMatchesKey(subscription, publicKey) {
  const saved = new Uint8Array(subscription?.options.applicationServerKey || []);
  const expected = publicKeyBytes(publicKey);
  return saved.length === expected.length && saved.every((byte, i) => byte === expected[i]);
}

export async function enablePush(publicKey) {
  // iOS에서도 사용자 클릭과 같은 호출 스택에서 권한을 요청한다.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('알림이 허용되지 않았습니다. 기기 또는 브라우저 설정에서 알림을 허용해주세요.');
  await reconcilePushOwner();
  const registration = await getPWARegistration();
  let subscription = await registration.pushManager.getSubscription();
  const key = publicKeyBytes(publicKey);
  if (subscription && !subscriptionMatchesKey(subscription, publicKey)) {
    await disablePush();
    subscription = null;
  }
  const created = !subscription;
  subscription ||= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  try {
    await pushAPI('subscriptions', 'POST', { ...subscription.toJSON(), publicKey });
    localStorage.setItem(OWNER_KEY, owner());
  } catch (error) {
    if (created) await subscription.unsubscribe();
    throw error;
  }
}

export async function disablePush() {
  const subscription = await currentSubscription();
  if (subscription) {
    await pushAPI('subscriptions', 'DELETE', { endpoint: subscription.endpoint });
    if (!(await subscription.unsubscribe())) throw new Error('기기의 알림 구독을 해제하지 못했습니다. 다시 시도해주세요.');
  }
  localStorage.removeItem(OWNER_KEY);
}

export async function clearPushOnLogout() {
  if (!('serviceWorker' in navigator)) return;
  // 인증 만료 시에도 기기의 구독과 표시된 알림을 직접 해제한다.
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) return;
  const subscription = await registration.pushManager?.getSubscription();
  try {
    if (subscription) {
      try {
        await fetch('/push/subscriptions', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: subscription.endpoint }), signal: AbortSignal.timeout(3000) });
      } finally {
        await subscription.unsubscribe();
      }
    }
  } finally {
    for (const notification of await registration.getNotifications()) notification.close();
    localStorage.removeItem(OWNER_KEY);
  }
}
